/**
 * useTemplateEditor Hook
 * Sprint 19: Custom Template Creation
 * 
 * Provides template editing functionality with validation and persistence
 */

import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  editingTemplateAtom,
  templateEditorModeAtom,
  templateEditorVisibleAtom,
  templateIsDirtyAtom,
  templateValidationErrorsAtom,
  activeSceneIndexAtom,
  activeSceneAtom,
  activeScriptAtom,
  initializeCreateModeAtom,
  initializeDuplicateModeAtom,
  initializeEditModeAtom,
  updateTemplateFieldAtom,
  addSceneAtom,
  removeSceneAtom,
  updateSceneAtom,
  updateScriptContentAtom,
  reorderScenesAtom,
  closeTemplateEditorAtom,
  isTemplateValidAtom
} from '../atoms/templateEditorAtoms';
import { Template, TemplateScene, TemplateScript } from '../../../types/template';
import templateService from '../services/templateService';
import logger from '../utils/logger';
import { PROJECT_LIMITS } from '../config/projectLimits';

export const useTemplateEditor = () => {
  const [editingTemplate, setEditingTemplate] = useAtom(editingTemplateAtom);
  const [mode] = useAtom(templateEditorModeAtom);
  const [isVisible] = useAtom(templateEditorVisibleAtom);
  const [isDirty] = useAtom(templateIsDirtyAtom);
  const [validationErrors, setValidationErrors] = useAtom(templateValidationErrorsAtom);
  const [activeSceneIndex, setActiveSceneIndex] = useAtom(activeSceneIndexAtom);
  const isValid = useAtomValue(isTemplateValidAtom);
  const activeScene = useAtomValue(activeSceneAtom);
  const activeScript = useAtomValue(activeScriptAtom);
  
  const initCreate = useSetAtom(initializeCreateModeAtom);
  const initDuplicate = useSetAtom(initializeDuplicateModeAtom);
  const initEdit = useSetAtom(initializeEditModeAtom);
  const updateField = useSetAtom(updateTemplateFieldAtom);
  const addScene = useSetAtom(addSceneAtom);
  const removeScene = useSetAtom(removeSceneAtom);
  const updateScene = useSetAtom(updateSceneAtom);
  const updateScript = useSetAtom(updateScriptContentAtom);
  const reorderScenes = useSetAtom(reorderScenesAtom);
  const closeEditor = useSetAtom(closeTemplateEditorAtom);

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    
    if (!editingTemplate) {
      errors['template'] = 'No template being edited';
      setValidationErrors(errors);
      return false;
    }
    
    if (!editingTemplate.title?.trim()) {
      errors['title'] = 'Title is required';
    }
    
    if (!editingTemplate.description?.trim()) {
      errors['description'] = 'Description is required';
    }
    
    if (!editingTemplate.category) {
      errors['category'] = 'Category is required';
    }
    
    if (!editingTemplate.scenes || editingTemplate.scenes.length === 0) {
      errors['scenes'] = 'At least one scene is required';
    } else {
      editingTemplate.scenes.forEach((scene, index) => {
        if (!scene.title?.trim()) {
          errors[`scene.${index}.title`] = `Scene ${index + 1}: Title is required`;
        }
        if (!scene.layoutPreset) {
          errors[`scene.${index}.layoutPreset`] = `Scene ${index + 1}: Layout is required`;
        }
        if (scene.suggestedDuration <= 0) {
          errors[`scene.${index}.suggestedDuration`] = `Scene ${index + 1}: Duration must be positive`;
        }
      });
    }
    
    if (!editingTemplate.scripts || editingTemplate.scripts.length === 0) {
      errors['scripts'] = 'At least one script is required';
    } else {
      editingTemplate.scripts.forEach((script, index) => {
        if (!script.content?.trim()) {
          errors[`script.${index}.content`] = `Script ${index + 1}: Content is required`;
        }
      });
    }
    
    if (!editingTemplate.exampleOutput?.title?.trim()) {
      errors['exampleOutput.title'] = 'Example output title is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [editingTemplate, setValidationErrors]);

  const saveTemplate = useCallback(async (): Promise<string | null> => {
    if (!validate()) {
      logger.warn('[useTemplateEditor] Validation failed, cannot save');
      return null;
    }
    
    if (!editingTemplate) {
      logger.error('[useTemplateEditor] No template to save');
      return null;
    }
    
    try {
      const completeTemplate = {
        ...editingTemplate,
        createdBy: editingTemplate.createdBy || 'user',
        isOfficial: false,
        updatedAt: Date.now()
      } as Omit<Template, 'id' | 'createdAt' | 'updatedAt'>;
      
      let savedId: string;
      
      if (mode === 'edit' && editingTemplate.id) {
        await templateService.updateCustomTemplate(editingTemplate.id, completeTemplate);
        savedId = editingTemplate.id;
        logger.info(`[useTemplateEditor] Template updated: ${savedId}`);
      } else {
        savedId = await templateService.createCustomTemplate(completeTemplate);
        logger.info(`[useTemplateEditor] Template created: ${savedId}`);
      }
      
      closeEditor();
      return savedId;
    } catch (error) {
      logger.error('[useTemplateEditor] Failed to save template:', error);
      throw error;
    }
  }, [editingTemplate, mode, validate, closeEditor]);

  const duplicateScene = useCallback((sceneIndex: number): { success: boolean; reason?: string } => {
    if (!editingTemplate?.scenes || sceneIndex < 0 || sceneIndex >= editingTemplate.scenes.length) {
      return { success: false, reason: 'invalid_scene' };
    }
    
    const currentSceneCount = editingTemplate.scenes.length;
    if (currentSceneCount >= PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE) {
      return { success: false, reason: 'max_scenes_reached' };
    }
    
    const sourceScene = editingTemplate.scenes[sceneIndex];
    const sourceScript = editingTemplate.scripts?.find(s => s.key === sourceScene.scriptKey);
    
    const newSceneIndex = editingTemplate.scenes.length;
    const newScriptKey = `script-${newSceneIndex}-${Date.now()}`;
    
    const newScene: TemplateScene = {
      ...sourceScene,
      order: newSceneIndex,
      title: `${sourceScene.title} (Copy)`,
      scriptKey: newScriptKey
    };
    
    const newScript: TemplateScript = sourceScript ? {
      ...sourceScript,
      key: newScriptKey,
      sceneTitle: newScene.title
    } : {
      key: newScriptKey,
      sceneTitle: newScene.title,
      content: '',
      defaultExample: ''
    };
    
    setEditingTemplate({
      ...editingTemplate,
      scenes: [...editingTemplate.scenes, newScene],
      scripts: [...(editingTemplate.scripts || []), newScript]
    });
    
    return { success: true };
  }, [editingTemplate, setEditingTemplate]);

  const getScriptForScene = useCallback((sceneIndex: number): TemplateScript | null => {
    if (!editingTemplate?.scenes || sceneIndex < 0 || sceneIndex >= editingTemplate.scenes.length) {
      return null;
    }
    
    const scene = editingTemplate.scenes[sceneIndex];
    return editingTemplate.scripts?.find(s => s.key === scene.scriptKey) || null;
  }, [editingTemplate]);

  const updateScriptForScene = useCallback((sceneIndex: number, content: string) => {
    if (!editingTemplate?.scenes || sceneIndex < 0 || sceneIndex >= editingTemplate.scenes.length) {
      return;
    }
    
    const scene = editingTemplate.scenes[sceneIndex];
    updateScript(scene.scriptKey, content);
  }, [editingTemplate, updateScript]);

  const getTotalDuration = useCallback((): number => {
    if (!editingTemplate?.scenes) return 0;
    return editingTemplate.scenes.reduce((total, scene) => total + scene.suggestedDuration, 0);
  }, [editingTemplate]);

  return {
    editingTemplate,
    mode,
    isVisible,
    isDirty,
    isValid,
    validationErrors,
    activeSceneIndex,
    activeScene,
    activeScript,
    
    setActiveSceneIndex,
    initCreate,
    initDuplicate,
    initEdit,
    updateField,
    addScene,
    removeScene,
    updateScene,
    updateScript,
    reorderScenes,
    closeEditor,
    
    validate,
    saveTemplate,
    duplicateScene,
    getScriptForScene,
    updateScriptForScene,
    getTotalDuration
  };
};

export default useTemplateEditor;
